<template>
  <Teleport v-if="toolbarTarget" :to="toolbarTarget">
    <div class="ip-menu">
      <button
        class="ip-btn"
        :class="{
          'ip-btn--active': visible && !isTargetMode && !isRangeMode,
          'ip-btn--target-active': visible && isTargetMode && !isRangeMode,
          'ip-btn--range-active': visible && isRangeMode,
        }"
        @click="toggleVisible"
        title="Atalho [I] para alternar visibilidade do Inventory Profile"
      >
        <span class="shortcut">[I]</span>
        Inventory Profile: <strong>{{ visible ? 'ON' : 'OFF' }}</strong>
        <span v-if="isTargetMode" class="ip-btn__hint">T</span>
        <span v-if="isRangeMode" class="ip-btn__hint ip-btn__hint--range">S</span>
      </button>

      <div class="ip-menu__panel">
        <div class="ip-toggle-row">
          <span class="ip-toggle-row__label">
            <span class="shortcut">[T]</span> Target Mode
          </span>
          <button
            type="button"
            class="ip-switch"
            :class="{ 'ip-switch--on': isTargetMode }"
            role="switch"
            :aria-checked="isTargetMode"
            @click="toggleTargetMode"
            title="Atalho [T] para alternar modo Target Fechamento"
          >
            <span class="ip-switch__opt" :class="{ 'is-active': !isTargetMode }">OFF</span>
            <span class="ip-switch__opt" :class="{ 'is-active': isTargetMode }">ON</span>
          </button>
        </div>

        <div class="ip-toggle-row">
          <span class="ip-toggle-row__label">
            <span class="shortcut">[S]</span> Range Select
          </span>
          <button
            type="button"
            class="ip-switch ip-switch--range"
            :class="{ 'ip-switch--on': isRangeMode }"
            role="switch"
            :aria-checked="isRangeMode"
            @click="toggleRangeMode"
            title="Atalho [S] para somar um intervalo de faixas"
          >
            <span class="ip-switch__opt" :class="{ 'is-active': !isRangeMode }">OFF</span>
            <span class="ip-switch__opt" :class="{ 'is-active': isRangeMode }">ON</span>
          </button>
        </div>

        <div
          v-if="!isTargetMode"
          class="ip-margin-control"
          title="Largura fixa das faixas do Inventory Profile em USD (grade absoluta, independente do preço atual)"
        >
          <label class="margin-lbl">Faixa:</label>
          <input
            type="number"
            v-model.number="dollarBinSize"
            step="50"
            min="50"
            max="5000"
            class="margin-input margin-input--usd"
          />
          <span class="margin-unit">USD</span>
        </div>

        <div
          v-else
          class="ip-margin-control"
          title="Ajustar percentual de margem/faixa (P_step = preço * margem%)"
        >
          <label class="margin-lbl">Margem:</label>
          <input
            type="number"
            v-model.number="marginInputPct"
            step="0.1"
            min="0.1"
            max="5.0"
            class="margin-input"
          />
          <span class="margin-unit">%</span>
        </div>

        <p v-if="isRangeMode" class="ip-range-help">
          Arraste as faixas para somar · clique até o preço atual · Esc limpa
        </p>
      </div>
    </div>
  </Teleport>

  <div
    v-if="visible && ready"
    ref="overlayRef"
    class="inventory-profile__overlay"
    :class="{
      'is-range-mode': isRangeMode,
      'has-selection': Boolean(selectionRange),
    }"
  >
    <div
      v-if="rangeBox"
      class="ip-range-band"
      :style="{ top: rangeBox.top + 'px', height: rangeBox.height + 'px' }"
    ></div>

    <div
      v-for="(bin, idx) in renderedBins"
      :key="bin.binFloor"
      v-show="bin.y !== null"
      class="ip-profile-row"
      :class="{ 'is-selected': isBinSelected(bin) }"
      :style="{ top: bin.y + 'px' }"
      @mouseenter="hoveredBinIdx = idx"
      @mouseleave="hoveredBinIdx = null"
    >
      <div class="ip-bin-guide" aria-hidden="true"></div>
      <InventoryProfileBookCard
        v-if="hoveredBinIdx === idx && !isRangeMode"
        class="ip-profile-tooltip"
        :stats="bookStats(bin)"
        :title="formatPriceRange(bin.priceMin, bin.priceMax)"
        :tag="isTargetMode ? ('Target ' + marginInputPct.toFixed(1) + '%') : ('$' + Math.round(dollarBinSize))"
        :tag-kind="isTargetMode ? 'target' : 'bin'"
        :is-target-mode="isTargetMode"
      />

      <div
        class="ip-bar ip-bar--short"
        :class="{ 'ip-bar--target-mode': isTargetMode && bin.shortQty > 0 }"
        :style="{ width: getBarWidth(bin.shortQty) + 'px' }"
      >
        <span class="bar-label">{{ bin.shortQty.toFixed(4) }}</span>
      </div>

      <div
        class="ip-bar ip-bar--long"
        :class="{ 'ip-bar--target-mode': isTargetMode && bin.longQty > 0 }"
        :style="{ width: getBarWidth(bin.longQty) + 'px' }"
      >
        <span class="bar-label">{{ bin.longQty.toFixed(4) }}</span>
      </div>
    </div>

    <div
      v-if="isRangeMode"
      class="ip-range-capture"
      aria-label="Selecionar intervalo de faixas"
      @mousedown.prevent="onRangeMouseDown"
    ></div>

    <InventoryProfileBookCard
      v-if="isRangeMode && rangeStats.binCount > 0"
      class="ip-profile-tooltip ip-range-hud"
      :style="hudStyle"
      :stats="rangeStats"
      :title="formatPriceRange(rangeStats.priceMin, rangeStats.priceMax)"
      :tag="rangeStats.binCount + ' faixa' + (rangeStats.binCount === 1 ? '' : 's')"
      tag-kind="range"
      :is-target-mode="isTargetMode"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useInventoryProfile } from './useInventoryProfile'
import InventoryProfileBookCard from './InventoryProfileBookCard.vue'
import {
  aggregateSelectedBins,
  envelopePriceRange,
  isSelectableBin,
  nearestBinByY,
  priceRangeFromAnchorToCurrent,
  selectBinsByPriceRange,
  selectBinsByYRange,
} from './domain/aggregateSelectedBins'

const DRAG_THRESHOLD_PX = 6
const CLICK_SNAP_PX = 24

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  priceToY: { type: Function, required: true },
  ready: { type: Boolean, default: false },
  toolbarTarget: { type: [Object, String], default: null },
})

const hoveredBinIdx = ref(null)
const renderedBins = reactive([])
const overlayRef = ref(null)
const selectionRange = ref(null)
const rangeSelecting = ref(false)
const rangeStartY = ref(null)
const rangeEndY = ref(null)

const {
  visible,
  isTargetMode,
  isRangeMode,
  dollarBinSize,
  marginInputPct,
  densityBins,
  toggleVisible,
  toggleTargetMode,
  toggleRangeMode,
} = useInventoryProfile()

const selectedBins = computed(() => {
  if (!selectionRange.value) return []
  return selectBinsByPriceRange(
    renderedBins,
    selectionRange.value.priceMin,
    selectionRange.value.priceMax
  )
})

const selectedFloorSet = computed(() => {
  return new Set(
    selectedBins.value.filter(isSelectableBin).map((bin) => bin.binFloor)
  )
})

const rangeStats = computed(() => aggregateSelectedBins(selectedBins.value))

const rangeBox = computed(() => {
  const ys = selectedBins.value
    .filter(isSelectableBin)
    .map((bin) => bin.y)
    .filter((y) => y != null && y > 0)

  if (ys.length) {
    const pad = 12
    const top = Math.min(...ys) - pad
    const bottom = Math.max(...ys) + pad
    return { top, height: Math.max(bottom - top, 24) }
  }

  if (rangeSelecting.value && rangeStartY.value != null && rangeEndY.value != null) {
    const top = Math.min(rangeStartY.value, rangeEndY.value)
    return {
      top,
      height: Math.max(Math.abs(rangeEndY.value - rangeStartY.value), 4),
    }
  }

  return null
})

const hudStyle = computed(() => {
  if (!rangeBox.value) return {}
  return {
    top: rangeBox.value.top + rangeBox.value.height / 2 + 'px',
  }
})

function formatPriceRange(priceMin, priceMax) {
  return `$${Math.round(Number(priceMin) || 0).toLocaleString('en-US')} - $${Math.round(Number(priceMax) || 0).toLocaleString('en-US')}`
}

function bookStats(bin) {
  return aggregateSelectedBins([bin], { includeEmpty: true })
}

function getBarWidth(qty) {
  const n = Number(qty) || 0
  if (n <= 0) return 52
  return Math.min(Math.max(n * 1800, 52), 150)
}

function binAnchorPrice(bin) {
  if (isTargetMode.value) {
    return (bin.priceMin + bin.priceMax) / 2
  }
  return bin.priceMin
}

function updateBinCoordinates() {
  if (!props.ready || typeof props.priceToY !== 'function' || !densityBins.value) return

  const nextBins = densityBins.value.map((bin) => {
    const y = props.priceToY(binAnchorPrice(bin))
    return {
      ...bin,
      y: y !== null && y > 0 ? y : null,
    }
  })

  renderedBins.splice(0, renderedBins.length, ...nextBins)
}

function localY(event) {
  const rect = overlayRef.value?.getBoundingClientRect()
  if (!rect) return null
  return event.clientY - rect.top
}

function applyPriceRange(nextRange) {
  selectionRange.value = nextRange
}

function snapYToBin(y) {
  const bin = nearestBinByY(renderedBins, y, CLICK_SNAP_PX)
  return bin && bin.y != null ? Number(bin.y) : null
}

function applyYRange(y0, y1) {
  if (y0 == null || y1 == null) {
    applyPriceRange(null)
    return
  }
  applyPriceRange(envelopePriceRange(selectBinsByYRange(renderedBins, y0, y1)))
}

function commitPriceRange(nextRange) {
  applyPriceRange(nextRange)
  if (!rangeStats.value.binCount) {
    selectionRange.value = null
  }
}

function isBinSelected(bin) {
  return selectedFloorSet.value.has(bin.binFloor)
}

function clearRangeSelection() {
  selectionRange.value = null
  rangeSelecting.value = false
  rangeStartY.value = null
  rangeEndY.value = null
}

function stopRangeDrag() {
  window.removeEventListener('mousemove', onRangeMouseMove)
  window.removeEventListener('mouseup', onRangeMouseUp)
  rangeSelecting.value = false
}

function onRangeMouseDown(event) {
  if (!isRangeMode.value || event.button !== 0) return
  const y = localY(event)
  if (y == null) return
  const snapped = snapYToBin(y)
  rangeSelecting.value = true
  rangeStartY.value = snapped
  rangeEndY.value = snapped ?? y
  if (snapped != null) applyYRange(snapped, snapped)
  else applyPriceRange(null)
  window.addEventListener('mousemove', onRangeMouseMove)
  window.addEventListener('mouseup', onRangeMouseUp)
}

function onRangeMouseMove(event) {
  if (!rangeSelecting.value) return
  const y = localY(event)
  if (y == null) return
  rangeEndY.value = y
  if (rangeStartY.value == null) {
    const snapped = snapYToBin(y)
    if (snapped == null) return
    rangeStartY.value = snapped
  }
  applyYRange(rangeStartY.value, y)
}

function onRangeMouseUp(event) {
  const end = localY(event) ?? rangeEndY.value
  let start = rangeStartY.value
  if (start == null && end != null) start = snapYToBin(end)
  stopRangeDrag()
  if (start == null || end == null) {
    applyPriceRange(null)
    return
  }

  if (Math.abs(end - start) < DRAG_THRESHOLD_PX) {
    const anchor = nearestBinByY(renderedBins, end, CLICK_SNAP_PX)
    commitPriceRange(priceRangeFromAnchorToCurrent(renderedBins, anchor))
    return
  }

  applyYRange(start, end)
  commitPriceRange(selectionRange.value)
}

function handleRangeKeyDown(event) {
  if (event.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return
  if (event.key !== 'Escape' || !isRangeMode.value) return
  if (selectionRange.value || rangeSelecting.value) {
    stopRangeDrag()
    clearRangeSelection()
    return
  }
  toggleRangeMode()
}

watch(isRangeMode, (enabled) => {
  if (!enabled) {
    stopRangeDrag()
    clearRangeSelection()
  }
})

watch(visible, (shown) => {
  if (!shown) {
    stopRangeDrag()
    clearRangeSelection()
  }
})

let rafId = null
function startSyncLoop() {
  function loop() {
    if (visible.value && props.ready) {
      updateBinCoordinates()
    }
    rafId = requestAnimationFrame(loop)
  }
  loop()
}

function stopSyncLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

onMounted(() => {
  startSyncLoop()
  window.addEventListener('keydown', handleRangeKeyDown)
})

onUnmounted(() => {
  stopSyncLoop()
  stopRangeDrag()
  window.removeEventListener('keydown', handleRangeKeyDown)
})
</script>

<style scoped>
.ip-menu {
  position: relative;
}

.ip-menu__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 220px;
  padding: 10px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  flex-direction: column;
  gap: 8px;
}

.ip-menu__panel::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.ip-menu:hover .ip-menu__panel,
.ip-menu:focus-within .ip-menu__panel {
  display: flex;
}

.ip-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ip-toggle-row__label {
  font-size: 12px;
  font-weight: 600;
  color: #c9d1d9;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ip-switch {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 999px;
  cursor: pointer;
}

.ip-switch--on {
  border-color: #e3b341;
}

.ip-switch--range.ip-switch--on {
  border-color: #58a6ff;
}

.ip-switch__opt {
  min-width: 36px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  color: #8b949e;
  text-align: center;
}

.ip-switch__opt.is-active {
  background: #30363d;
  color: #f0f6fc;
}

.ip-switch--on .ip-switch__opt.is-active {
  background: rgba(227, 179, 65, 0.2);
  color: #e3b341;
}

.ip-switch--range.ip-switch--on .ip-switch__opt.is-active {
  background: rgba(88, 166, 255, 0.2);
  color: #58a6ff;
}

.ip-margin-control {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #21262d;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 3px 8px;
}

.margin-lbl {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.margin-input {
  width: 44px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #58a6ff;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  padding: 2px 0;
}

.margin-input--usd {
  width: 56px;
}

.margin-input:focus {
  outline: none;
  border-color: #58a6ff;
}

.margin-unit {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.ip-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.ip-btn:hover {
  background: #30363d;
  border-color: #8b949e;
}

.ip-btn--active {
  background: rgba(35, 134, 54, 0.2);
  color: #3fb950;
  border-color: #2ea043;
}

.ip-btn--target-active {
  background: rgba(227, 179, 65, 0.2);
  color: #e3b341;
  border-color: #e3b341;
}

.ip-btn--range-active {
  background: rgba(88, 166, 255, 0.18);
  color: #58a6ff;
  border-color: #58a6ff;
}

.ip-btn__hint {
  margin-left: 2px;
  background: rgba(227, 179, 65, 0.2);
  color: #e3b341;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid #e3b341;
}

.ip-btn__hint--range {
  background: rgba(88, 166, 255, 0.2);
  color: #58a6ff;
  border-color: #58a6ff;
}

.shortcut {
  color: #8b949e;
  font-weight: 400;
  font-size: 11px;
}

.inventory-profile__overlay {
  position: absolute;
  top: 0;
  right: 75px;
  bottom: 0;
  width: 280px;
  pointer-events: none;
  z-index: 10;
  overflow: visible;
}

.ip-range-help {
  margin: 0;
  padding: 6px 8px;
  background: rgba(88, 166, 255, 0.08);
  border: 1px solid rgba(88, 166, 255, 0.35);
  border-radius: 6px;
  color: #9ecbff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
}

.ip-range-band {
  position: absolute;
  right: 0;
  width: 25vw;
  transform: translateX(calc(-100% + 6px));
  background: rgba(88, 166, 255, 0.12);
  border-top: 1px solid rgba(88, 166, 255, 0.55);
  border-bottom: 1px solid rgba(88, 166, 255, 0.55);
  pointer-events: none;
  z-index: 1;
}

.ip-range-capture {
  position: absolute;
  inset: 0;
  z-index: 12;
  pointer-events: auto;
  cursor: ns-resize;
}

.ip-range-hud {
  right: 100%;
  margin-right: 8px;
  transform: translateY(-50%);
  z-index: 18;
  pointer-events: none;
  border-color: #388bfd;
}

.ip-profile-row {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  transform: translateY(-50%);
  pointer-events: auto;
  cursor: pointer;
  padding: 2px 0;
  z-index: 2;
}

.ip-bin-guide {
  position: absolute;
  top: 50%;
  right: calc(100% - 6px);
  width: 25vw;
  border-top: 1px dashed rgba(255, 255, 255, 0.67);
  pointer-events: none;
}

.ip-bar {
  height: 20px;
  min-width: 52px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-sizing: border-box;
  opacity: 0.88;
  transition: all 0.15s ease;
}

.ip-bar:hover {
  opacity: 1;
  transform: scale(1.02);
}

.inventory-profile__overlay.has-selection .ip-bar {
  opacity: 0.32;
}

.inventory-profile__overlay.has-selection .ip-profile-row.is-selected .ip-bar {
  opacity: 1;
  box-shadow: 0 0 0 1px #58a6ff, 0 0 10px rgba(88, 166, 255, 0.35);
}

.ip-bar--long {
  background-color: #2e7d32;
  border: 1px solid #4caf50;
}

.ip-bar--short {
  background-color: #c62828;
  border: 1px solid #ef5350;
}

.ip-bar--target-mode {
  box-shadow: 0 0 6px rgba(227, 179, 65, 0.6);
}

.bar-label {
  font-size: 10px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  white-space: nowrap;
}

.ip-profile-tooltip {
  position: absolute;
  right: 100%;
  margin-right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: #1e2430;
  border: 1px solid #4b5563;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 25;
}
</style>
